import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';
import { EditIcon } from '@shared/ui/icons';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { DetailCard, DetailSection, SectionAction } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import type { AdminUserDetail } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { biographyFormSchema, type BiographyFormValues } from './forms.schema';
import { useUpdateBiography } from './mutations';

/**
 * §3 النبذة — a section of its own, and it exists only because the account holds
 * a role.
 *
 * **Absent, never empty.** The catalog confines the biography to a role-holder's
 * profile and collects none at registration, so a subscriber's detail renders no
 * §3 at all. `backend-auth` expresses the same rule on the wire by **omitting
 * the key** rather than serving `null` — a `null` would draw an editable field
 * on an account the platform gives no biography to edit. The caller decides
 * whether to mount this section by asking whether the account holds a role, and
 * this component renders whatever the key holds once it is mounted.
 *
 * **It is not part of §1.** A paragraph about a person sitting beneath their
 * phone number reads as an operator's note *about* them; as its own section with
 * its own heading and its own verb it reads as what it is — the account's public
 * text, which the operator may maintain because the platform answers for it.
 *
 * **No character counter.** No catalog row declares a limit, and a counter would
 * promise a constraint the service does not enforce.
 */
export function BiographySection({ user }: { user: AdminUserDetail }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const updateBiography = useUpdateBiography(user.id);
  const [formError, setFormError] = useState<string | null>(null);

  function submit(values: BiographyFormValues) {
    setFormError(null);

    updateBiography.mutate(values.biography, {
      onSuccess: () => {
        setOpen(false);
        toast.show(copy.biography.saved, 'success');
      },
      onError: (error) => {
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        // The refusal stays on the dialog rather than in a toast: the operator's
        // paragraph is still on screen and is still theirs to correct.
        setFormError(resolveErrorCopy(code, status, errors).message);
      },
    });
  }

  return (
    <>
      <DetailSection
        id="user-biography"
        heading={copy.biography.heading}
        context={copy.biography.context}
        action={
          <SectionAction onClick={() => setOpen(true)} icon={<EditIcon />}>
            {copy.biography.edit}
          </SectionAction>
        }
      >
        <DetailCard>
          <div className="p-4">
            <p className="mb-0 max-w-measure text-sm text-ink-muted">
              {user.biography ?? copy.biography.empty}
            </p>
          </div>
        </DetailCard>
      </DetailSection>

      <BiographyDialog
        open={open}
        user={user}
        busy={updateBiography.isPending}
        formError={formError}
        onSubmit={submit}
        onCancel={() => {
          setFormError(null);
          setOpen(false);
        }}
      />
    </>
  );
}

/**
 * Wider than its siblings, because what it holds is a paragraph rather than a
 * line: Arabic prose in a narrow dialog becomes a column four words across,
 * which is the failure the reading-measure rule exists to prevent. The textarea
 * grows on the block axis only — horizontal growth breaks the dialog's width.
 */
function BiographyDialog({
  open,
  user,
  busy,
  formError,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  user: AdminUserDetail;
  busy: boolean;
  formError: string | null;
  onSubmit: (values: BiographyFormValues) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const fieldId = useId();
  const hintId = useId();
  const errorId = useId();
  const alertId = useId();

  const { register, handleSubmit, reset, formState } = useForm<BiographyFormValues>({
    resolver: zodResolver(biographyFormSchema),
    defaultValues: { biography: user.biography ?? '' },
  });

  useEffect(() => {
    if (!open) return;
    reset({ biography: user.biography ?? '' });
  }, [open, user.biography, reset]);

  const fieldError = formState.errors.biography?.message;
  // The error joins `aria-describedby` when it appears and leaves when it goes,
  // so no hidden text is announced.
  const describedBy = [hintId, fieldError ? errorId : null].filter(Boolean).join(' ');

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      className="max-w-[34rem]"
      onClose={(returnValue) => {
        if (returnValue !== 'save') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of saving on the way out.
          if (isCancelledSubmit(event)) return;
          event.preventDefault();
          void handleSubmit(onSubmit)(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.biography.dialog.title(user.display_name)}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.biography.dialog.requiredNote}</p>
        </div>

        {formError ? (
          <p id={alertId} role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <div className="grid gap-2">
          <label htmlFor={fieldId} className="text-sm font-medium text-ink">
            {copy.biography.dialog.label}
            <span aria-hidden="true" className="ms-px text-danger">
              *
            </span>
          </label>

          <textarea
            id={fieldId}
            rows={5}
            aria-describedby={describedBy}
            aria-invalid={Boolean(fieldError)}
            aria-required
            className="w-full rounded-sm border border-border-strong bg-surface p-3 text-sm leading-normal text-ink [min-block-size:7rem] [resize:vertical]"
            {...register('biography')}
          />

          <p id={hintId} className="mb-0 max-w-none text-xs text-ink-muted">
            {copy.biography.dialog.hint}
          </p>

          {fieldError ? (
            <p id={errorId} className="mb-0 max-w-none text-xs text-danger">
              {fieldError}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="save"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.biography.dialog.save}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
